import React, { useEffect, useState } from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { CheckIcon } from "@heroicons/react/20/solid"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { TAbrandCategories } from "../../../../services/userAPI"

interface SubCategory {
  name: string
  count: number
}

interface Category {
  main_category: string
  sub_categories: SubCategory[]
}

interface CategoryModalProps {
  title: string
  isOpen: boolean
  onClose: () => void
  onSelect: (category: string) => void
  selectedValue?: { category?: string }
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  title,
  isOpen,
  onClose,
  onSelect,
  selectedValue,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      fetchUserCategories()
      if (selectedValue?.category) {
        const categoriesArray = selectedValue.category
          .split(",")
          .map((cat) => cat.trim())
          .filter((cat) => cat.length > 0)
        setSelectedCategories(categoriesArray)
      } else {
        setSelectedCategories([])
      }
    }
  }, [isOpen, selectedValue])

  const fetchUserCategories = async () => {
    const response = await TAbrandCategories()
    if (response && response.data) {
      setCategories(response.data)
    }
  }

  const filteredCategories = categories
    .map((category) => {
      const filteredSubs = category.sub_categories.filter((sub) =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      return { ...category, sub_categories: filteredSubs }
    })
    .filter((category) => category.sub_categories.length > 0)

  const handleCategorySelect = (subCategory: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(subCategory)) {
        return prev.filter((cat) => cat !== subCategory)
      } else {
        return [...prev, subCategory]
      }
    })
  }

  const handleMainCategorySelect = (mainCategory: string) => {
    const category = categories.find((cat) => cat.main_category === mainCategory)
    if (!category) {
      return
    }

    const allSubCategories = category.sub_categories.map((sub) => sub.name)
    const allSelected = allSubCategories.every((sub) => selectedCategories.includes(sub))

    if (allSelected) {
      setSelectedCategories((prev) => prev.filter((cat) => !allSubCategories.includes(cat)))
    } else {
      setSelectedCategories((prev) => {
        const newCategories = [...prev]
        allSubCategories.forEach((sub) => {
          if (!newCategories.includes(sub)) {
            newCategories.push(sub)
          }
        })
        return newCategories
      })
    }
  }

  const isSelected = (subCategory: string) => {
    return selectedCategories.includes(subCategory)
  }

  const isMainCategoryFullySelected = (mainCategory: string) => {
    const category = categories.find((cat) => cat.main_category === mainCategory)
    if (!category || category.sub_categories.length === 0) {
      return false
    }

    const allSubCategories = category.sub_categories.map((sub) => sub.name)
    return allSubCategories.every((sub) => selectedCategories.includes(sub))
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-semibold">{title}</h2>
              <p className="text-base opacity-90 mt-1">Kategori Seçimi</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Search Input */}
          <div className="relative w-full mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kategori Ara"
              className="w-full pl-12 pr-4 py-3 text-base bg-gray-100 rounded-2xl border-none focus:ring-0 focus:outline-none"
            />
          </div>

          {/* Categories List */}
          <div className="space-y-3">
            {filteredCategories.length === 0 ? (
              <div className="px-4 py-3 text-base text-gray-500 text-center">
                Kategori Bulunamadı
              </div>
            ) : (
              filteredCategories.map((category) => (
                <div key={category.main_category} className="space-y-2">
                  <h3
                    className="font-semibold text-gray-800 text-base cursor-pointer hover:text-blue-600 flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => handleMainCategorySelect(category.main_category)}
                  >
                    {category.main_category}
                    {isMainCategoryFullySelected(category.main_category) && (
                      <CheckIcon className="w-5 h-5 text-blue-600" />
                    )}
                  </h3>
                  <div className="space-y-1 ml-4">
                    {category.sub_categories.map((subCategory) => (
                      <div
                        key={subCategory.name}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleCategorySelect(subCategory.name)}
                      >
                        <span className="text-base text-gray-700">{subCategory.name}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">({subCategory.count})</span>
                          {isSelected(subCategory.name) && (
                            <CheckIcon className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-base text-gray-600">
              {selectedCategories.length > 0
                ? `${selectedCategories.length} kategori seçili`
                : "Kategori seçilmedi"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-base text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  if (selectedCategories.length > 0) {
                    onSelect(selectedCategories.join(", "))
                  }
                  onClose()
                }}
                disabled={selectedCategories.length === 0}
                className="px-4 py-2 text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CategoryModal
